import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [fileType, setFileType] = useState(''); // 'image' or 'video'
  const [title, setTitle] = useState(''); // ← ADDED TITLE STATE
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    navigate('/admin-login');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if it's image or video
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      
      if (!isImage && !isVideo) {
        setMessage('❌ Please select a valid image or video file.');
        return;
      }

      // Check file size (50MB limit for videos, 10MB for images)
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setMessage(`❌ File too large. Maximum size is ${isVideo ? '50MB' : '10MB'}.`);
        return;
      }

      setSelectedFile(file);
      setFileType(isVideo ? 'video' : 'image');
      setMessage('');

      // Create preview
      if (isImage) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        // For videos, we'll just show a video icon and file info
        setFilePreview('video');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile || !title.trim()) { // ← ADDED TITLE CHECK
      setMessage('❌ Please add a title and select a file.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', title); // ← ADDED TITLE
      formData.append('description', description);

      const response = await fetch(`${API_URL}/posts`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('✅ Post published successfully!');
        // Reset form
        setSelectedFile(null);
        setFilePreview('');
        setFileType('');
        setTitle(''); // ← RESET TITLE
        setDescription('');
        // Clear file input
        document.getElementById('file-input').value = '';
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ Error: ${data.message || 'Failed to publish post'}`);
      }
    } catch (error) {
      setMessage('❌ Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview('');
    setFileType('');
    setTitle(''); // ← RESET TITLE
    document.getElementById('file-input').value = '';
  };

  const getFileInfo = () => {
    if (!selectedFile) return null;
    
    const sizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
    return {
      name: selectedFile.name,
      size: sizeInMB,
      type: selectedFile.type
    };
  };

  const fileInfo = getFileInfo();

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>🏗️ Saudigram Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>

      <div className="dashboard-content">
        <div className="create-post-form">
          <h2>Create New Post</h2>
          
          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* ADDED TITLE INPUT FIELD */}
            <div className="form-group">
              <label>🏷️ Post Title:</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter a compelling title for your post..."
                required
                className="title-input"
              />
              <small>Make it engaging! Example: "NEOM Construction Reaches 35% Completion"</small>
            </div>

            <div className="form-group">
              <label>Upload File:</label>
              <div 
                className="file-upload-area" 
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <div className="upload-placeholder">
                  {filePreview ? (
                    <div className="file-preview">
                      {fileType === 'video' ? (
                        <div className="video-preview">
                          <div className="video-icon">🎥</div>
                          <p>Video Selected</p>
                          <div className="file-info">
                            <strong>{fileInfo.name}</strong>
                            <small>{fileInfo.size} MB • {fileInfo.type}</small>
                          </div>
                        </div>
                      ) : (
                        <div className="image-preview">
                          <img src={filePreview} alt="Preview" />
                          <div className="file-info">
                            <strong>{fileInfo.name}</strong>
                            <small>{fileInfo.size} MB • {fileInfo.type}</small>
                          </div>
                        </div>
                      )}
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering file input
                          removeFile();
                        }}
                        className="remove-file-btn"
                      >
                        ✕ Remove File
                      </button>
                    </div>
                  ) : (
                    <div className="upload-instructions">
                      <div className="upload-icon">📸🎥</div>
                      <p><strong>Click here to select photo or video</strong></p>
                      <div className="supported-formats">
                        <div className="format-item">
                          <span className="format-icon">📷</span>
                          <div>
                            <strong>Images</strong>
                            <div>JPEG, PNG, GIF, WebP</div>
                            <small>Max 10MB</small>
                          </div>
                        </div>
                        <div className="format-item">
                          <span className="format-icon">🎥</span>
                          <div>
                            <strong>Videos</strong>
                            <div>MP4, MOV, AVI, WebM</div>
                            <small>Max 50MB</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Write your detailed project update here...
                
You can use multiple paragraphs and bullet points:
• Progress updates
• Key achievements  
• Challenges faced
• Future plans"
                rows="12"
                required
              />
              <small>Write as much detail as you want about the project progress</small>
            </div>

            <button 
              type="submit" 
              className="publish-btn"
              disabled={loading || !selectedFile}
            >
              {loading ? '📤 Uploading...' : '📤 Publish Post'}
            </button>
          </form>
        </div>

        <div className="dashboard-info">
          <h3>📝 Posting Guidelines</h3>
          <ul>
            <li>Use high-quality construction photos/videos</li>
            <li>Write detailed progress updates</li>
            <li>Include key milestones and achievements</li>
            <li>Mention challenges and solutions</li>
            <li>Share future plans and timelines</li>
          </ul>
          
          <h3>📁 File Requirements</h3>
          <ul>
            <li><strong>Images:</strong> JPEG, PNG, GIF, WebP (Max 10MB)</li>
            <li><strong>Videos:</strong> MP4, MOV, AVI, WebM (Max 50MB)</li>
            <li>High resolution for best quality</li>
            <li>Landscape orientation recommended</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;